import { useState, useEffect } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Table, { Column } from '../../components/Table';
import Modal from '../../components/Modal';
import LookupSelect, { LookupOption } from '../../components/LookupSelect';

const SLOTS = ['ATTENDANCE', 'HEALTH', 'TRANSPORT', 'MEAL', 'OTHER1', 'OTHER2'];

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await api.get('/employees');
  const employees = res.data.employees || [];
  const filtered = search ? employees.filter((e: any) => e.full_name.toLowerCase().includes(search.toLowerCase())) : employees;
  return filtered.slice(0, 20).map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function Allowances() {
  const [tab, setTab] = useState<'daily' | 'staff'>('daily');
  const [dailyAllowances, setDailyAllowances] = useState<any[]>([]);
  const [staffAllowances, setStaffAllowances] = useState<any[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');

  const [showDailyForm, setShowDailyForm] = useState(false);
  const [dailyForm, setDailyForm] = useState({
    code: '', name: '', amount: 0, allowanceSlot: 'ATTENDANCE', conditionOperator: 'AND',
    checkByShift: false, checkByReason: false, applyOnMatching: true,
  });
  const [conditionsModalId, setConditionsModalId] = useState<string | null>(null);
  const [conditionRows, setConditionRows] = useState<{ conditionType: string; conditionValue: string }[]>([]);

  const [staffForm, setStaffForm] = useState({
    allowanceType: 'ATTENDANCE', monthlyAmount: 0, fullDeduction: false, fullDeductionThreshold: 0,
    partialDeduction1: 0, partialDeduction2: 0,
  });
  const [copyModalId, setCopyModalId] = useState<string | null>(null);
  const [copyEmployeeIds, setCopyEmployeeIds] = useState<string[]>([]);

  useEffect(() => {
    fetchDaily();
  }, []);

  useEffect(() => {
    if (selectedEmployee) fetchStaff(selectedEmployee);
  }, [selectedEmployee]);

  const fetchDaily = async () => {
    const res = await api.get('/tms/daily-allowances');
    setDailyAllowances(res.data.allowances || []);
  };

  const fetchStaff = async (employeeId: string) => {
    const res = await api.get(`/tms/staff-allowances/${employeeId}`);
    setStaffAllowances(res.data.allowances || []);
  };

  const createDaily = async () => {
    if (!dailyForm.code || !dailyForm.name || !dailyForm.amount) return alert('Code, name, amount required');
    await api.post('/tms/daily-allowances', dailyForm);
    setShowDailyForm(false);
    setDailyForm({ code: '', name: '', amount: 0, allowanceSlot: 'ATTENDANCE', conditionOperator: 'AND', checkByShift: false, checkByReason: false, applyOnMatching: true });
    fetchDaily();
  };

  const deleteDaily = async (id: string) => {
    if (!window.confirm('Delete this allowance?')) return;
    await api.delete(`/tms/daily-allowances/${id}`);
    fetchDaily();
  };

  const openConditions = async (id: string) => {
    const res = await api.get(`/tms/daily-allowances/${id}/conditions`);
    setConditionRows((res.data.conditions || []).map((c: any) => ({ conditionType: c.condition_type, conditionValue: c.condition_value })));
    setConditionsModalId(id);
  };

  const saveConditions = async () => {
    await api.put(`/tms/daily-allowances/${conditionsModalId}/conditions`, { conditions: conditionRows });
    setConditionsModalId(null);
  };

  const createStaffAllowance = async () => {
    if (!selectedEmployee) return alert('Select an employee first');
    await api.post('/tms/staff-allowances', { employeeId: selectedEmployee, ...staffForm });
    setStaffForm({ allowanceType: 'ATTENDANCE', monthlyAmount: 0, fullDeduction: false, fullDeductionThreshold: 0, partialDeduction1: 0, partialDeduction2: 0 });
    fetchStaff(selectedEmployee);
  };

  const deleteStaffAllowance = async (id: string) => {
    if (!window.confirm('Delete this staff allowance?')) return;
    await api.delete(`/tms/staff-allowances/${id}`);
    fetchStaff(selectedEmployee);
  };

  const doCopy = async () => {
    if (!copyEmployeeIds.length) return alert('Select at least one employee');
    await api.post(`/tms/staff-allowances/${copyModalId}/copy`, { employeeIds: copyEmployeeIds });
    setCopyModalId(null);
    setCopyEmployeeIds([]);
    alert('Copied');
  };

  const dailyColumns: Column<any>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'allowance_slot', header: 'Slot' },
    { key: 'amount', header: 'Amount', render: (r) => `$${r.amount}` },
    { key: 'condition_operator', header: 'Operator' },
    { key: 'actions', header: '', render: (r) => (
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => openConditions(r.id)}>Conditions</Button>
        <Button variant="danger" onClick={() => deleteDaily(r.id)}>Delete</Button>
      </div>
    ) },
  ];

  const staffColumns: Column<any>[] = [
    { key: 'allowance_type', header: 'Slot' },
    { key: 'monthly_amount', header: 'Monthly Amount', render: (r) => `$${r.monthly_amount || 0}` },
    { key: 'full_deduction', header: 'Full Deduction', render: (r) => (r.full_deduction ? 'Yes' : 'No') },
    { key: 'partial_deduction_1', header: '1st Deduction' },
    { key: 'partial_deduction_2', header: '2nd Deduction' },
    { key: 'actions', header: '', render: (r) => (
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => setCopyModalId(r.id)}>Copy to...</Button>
        <Button variant="danger" onClick={() => deleteStaffAllowance(r.id)}>Delete</Button>
      </div>
    ) },
  ];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Allowances</h1>
      <div className="flex gap-2 mb-4 border-b">
        {(['daily', 'staff'] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm font-medium ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}>
            {t === 'daily' ? 'Daily Allowance Master' : 'Staff Allowance Assignment'}
          </button>
        ))}
      </div>

      {tab === 'daily' && (
        <>
          <div className="flex justify-end mb-4">
            <Button onClick={() => setShowDailyForm(!showDailyForm)}>{showDailyForm ? 'Cancel' : 'Add Daily Allowance'}</Button>
          </div>
          {showDailyForm && (
            <div className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Input label="Code (max 6)" maxLength={6} value={dailyForm.code} onChange={(e) => setDailyForm({ ...dailyForm, code: e.target.value })} />
                <Input label="Name (max 50)" maxLength={50} value={dailyForm.name} onChange={(e) => setDailyForm({ ...dailyForm, name: e.target.value })} />
                <Input label="Amount" type="number" value={dailyForm.amount} onChange={(e) => setDailyForm({ ...dailyForm, amount: Number(e.target.value) })} />
                <Select label="Allowance Slot" value={dailyForm.allowanceSlot} onChange={(e) => setDailyForm({ ...dailyForm, allowanceSlot: e.target.value })} options={SLOTS.map((s) => ({ value: s, label: s }))} />
                <Select label="Condition Operator" value={dailyForm.conditionOperator} onChange={(e) => setDailyForm({ ...dailyForm, conditionOperator: e.target.value })} options={[{ value: 'AND', label: 'AND — all conditions must match' }, { value: 'OR', label: 'OR — any condition matches' }, { value: 'ADD', label: 'ADD (treated as OR)' }]} />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={dailyForm.applyOnMatching} onChange={(e) => setDailyForm({ ...dailyForm, applyOnMatching: e.target.checked })} />
                Pay when conditions match (uncheck to pay on non-matching days instead)
              </label>
              <Button onClick={createDaily}>Save</Button>
            </div>
          )}
          <Table columns={dailyColumns} rows={dailyAllowances} emptyMessage="No daily allowances configured" />
        </>
      )}

      {tab === 'staff' && (
        <>
          <div className="bg-white rounded-lg shadow p-4 mb-4">
            <LookupSelect label="Employee" value={selectedEmployee} onChange={setSelectedEmployee} fetchOptions={fetchEmployeeOptions} placeholder="Search employee..." />
          </div>
          {selectedEmployee && (
            <>
              <div className="bg-white rounded-lg shadow p-6 mb-6 space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <Select label="Slot" value={staffForm.allowanceType} onChange={(e) => setStaffForm({ ...staffForm, allowanceType: e.target.value })} options={SLOTS.map((s) => ({ value: s, label: s }))} />
                  <Input label="Monthly Amount" type="number" value={staffForm.monthlyAmount} onChange={(e) => setStaffForm({ ...staffForm, monthlyAmount: Number(e.target.value) })} />
                  <Input label="Full Deduction Threshold (min)" type="number" value={staffForm.fullDeductionThreshold} onChange={(e) => setStaffForm({ ...staffForm, fullDeductionThreshold: Number(e.target.value) })} />
                  <Input label="1st Partial Deduction" type="number" value={staffForm.partialDeduction1} onChange={(e) => setStaffForm({ ...staffForm, partialDeduction1: Number(e.target.value) })} />
                  <Input label="2nd Partial Deduction" type="number" value={staffForm.partialDeduction2} onChange={(e) => setStaffForm({ ...staffForm, partialDeduction2: Number(e.target.value) })} />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={staffForm.fullDeduction} onChange={(e) => setStaffForm({ ...staffForm, fullDeduction: e.target.checked })} />
                  Full deduction if absence/lateness reaches the threshold
                </label>
                <Button onClick={createStaffAllowance}>Add</Button>
              </div>
              <Table columns={staffColumns} rows={staffAllowances} emptyMessage="No staff allowances assigned" />
            </>
          )}
        </>
      )}

      <Modal isOpen={!!conditionsModalId} onClose={() => setConditionsModalId(null)} title="Allowance Conditions">
        <div className="space-y-3">
          {conditionRows.map((c, i) => (
            <div key={i} className="flex gap-2">
              <Select
                value={c.conditionType}
                onChange={(e) => setConditionRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, conditionType: e.target.value } : r)))}
                options={[{ value: 'shift', label: 'Shift' }, { value: 'reason', label: 'Reason' }, { value: 'device', label: 'Device' }]}
              />
              <Input value={c.conditionValue} onChange={(e) => setConditionRows((rows) => rows.map((r, idx) => (idx === i ? { ...r, conditionValue: e.target.value } : r)))} placeholder="value (e.g. shift ID or REST)" />
              <Button variant="danger" onClick={() => setConditionRows((rows) => rows.filter((_, idx) => idx !== i))}>Remove</Button>
            </div>
          ))}
          <Button variant="secondary" onClick={() => setConditionRows([...conditionRows, { conditionType: 'shift', conditionValue: '' }])}>Add Condition</Button>
          <Button className="w-full" onClick={saveConditions}>Save</Button>
        </div>
      </Modal>

      <Modal isOpen={!!copyModalId} onClose={() => setCopyModalId(null)} title="Copy Allowance to Other Staff">
        <LookupSelect
          label="Employees"
          value=""
          onChange={(v) => setCopyEmployeeIds((ids) => (ids.includes(v) ? ids : [...ids, v]))}
          fetchOptions={fetchEmployeeOptions}
          placeholder="Add employee..."
        />
        <div className="text-sm text-gray-600 my-2">{copyEmployeeIds.length} employee(s) selected</div>
        <Button className="w-full" onClick={doCopy}>Copy</Button>
      </Modal>
    </div>
  );
}
